import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import {
  ElectricBolt as ElectricBoltIcon,
  Business as BusinessIcon,
  TrendingUp as TrendingUpIcon,
  CheckCircle as CheckCircleIcon,
  Category as CategoryIcon,
  Timeline as TimelineIcon,
  People as PeopleIcon,
} from '@mui/icons-material';
import { metricsService, CPEMetricsData } from '../services/metricsService';
import { CountUp } from '../components/CountUp';
import { EnergyDecor } from '../components/EnergyDecor';
import { animations, transitions, getStaggerDelay } from '../theme/animations';

export const CPEMetricsPage = () => {
  const { t } = useTranslation();
  const [metrics, setMetrics] = useState<CPEMetricsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadMetrics();
  }, []);

  const loadMetrics = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await metricsService.getCPEMetrics();
      setMetrics(data);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string; errors?: Record<string, string> }; status?: number } };
      console.error('Failed to load metrics:', err);
      setError(error.response?.data?.message || 'Failed to load metrics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ my: 4 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      </Container>
    );
  }

  if (!metrics) {
    return null;
  }

  const { overview, byPlantType, powerByPlantType, byCPEStatus, powerByCPEStatus, byCommercialStatus, byInternalPositioning, byEnergizationYear, powerByEnergizationYear, topAccountManagers } = metrics;

  return (
    <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
      <Box sx={{ my: 4 }}>
          {/* Header */}
          <Box sx={{
            mb: 4,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            animation: animations.slideInLeft,
          }}>
            <EnergyDecor variant="lightning" size={60} animated />
            <Box>
              <Typography variant="h4" component="h1">
                {t('metrics.title', 'CPE Metrics & Statistics')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {t('metrics.subtitle', 'Real-time insights into renewable energy projects')}
              </Typography>
            </Box>
          </Box>

          {/* Overview Cards */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, mt: 4 }}>
            <EnergyDecor variant="battery" size={32} animated={false} />
            <Typography variant="h5">
              {t('metrics.overview', 'Overview')}
            </Typography>
          </Box>
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3} sx={{ animation: animations.scaleIn, animationDelay: getStaggerDelay(0) }}>
            <Card sx={{
              height: '100%',
              minHeight: 140,
              transition: transitions.default,
              '&:hover': {
                transform: 'translateY(-4px) scale(1.02)',
                boxShadow: 6,
              },
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <BusinessIcon color="primary" />
                  <Typography variant="h6" color="text.secondary">
                    {t('metrics.totalCPEs', 'Total CPEs')}
                  </Typography>
                </Box>
                <Typography variant="h3" color="primary.main">
                  <CountUp end={overview.totalCPEs} />
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3} sx={{ animation: animations.scaleIn, animationDelay: getStaggerDelay(1) }}>
            <Card sx={{
              height: '100%',
              minHeight: 140,
              transition: transitions.default,
              '&:hover': {
                transform: 'translateY(-4px) scale(1.02)',
                boxShadow: 6,
              },
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <ElectricBoltIcon color="secondary" />
                  <Typography variant="h6" color="text.secondary">
                    {t('metrics.totalPower', 'Total Power')}
                  </Typography>
                </Box>
                <Typography variant="h3" color="secondary.main">
                  <CountUp end={Math.round(overview.totalPower)} />
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  MW
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3} sx={{ animation: animations.scaleIn, animationDelay: getStaggerDelay(2) }}>
            <Card sx={{
              height: '100%',
              minHeight: 140,
              transition: transitions.default,
              '&:hover': {
                transform: 'translateY(-4px) scale(1.02)',
                boxShadow: 6,
              },
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <TrendingUpIcon color="success" />
                  <Typography variant="h6" color="text.secondary">
                    {t('metrics.averagePower', 'Average Power')}
                  </Typography>
                </Box>
                <Typography variant="h3" color="success.main">
                  <CountUp end={overview.averagePower} decimals={2} />
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  MW
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3} sx={{ animation: animations.scaleIn, animationDelay: getStaggerDelay(3) }}>
            <Card sx={{
              height: '100%',
              minHeight: 140,
              transition: transitions.default,
              '&:hover': {
                transform: 'translateY(-4px) scale(1.02)',
                boxShadow: 6,
              },
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <CheckCircleIcon sx={{ color: '#1d388d' }} />
                  <Typography variant="h6" color="text.secondary">
                    {t('metrics.waldevarProjects', 'Waldevar Projects')}
                  </Typography>
                </Box>
                <Typography variant="h3" sx={{ color: '#1d388d' }}>
                  <CountUp end={overview.waldevarProjects} />
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  <CountUp end={Math.round(overview.waldevarProjectsPower)} /> MW
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3} sx={{ animation: animations.scaleIn, animationDelay: getStaggerDelay(4) }}>
            <Card sx={{
              height: '100%',
              minHeight: 140,
              transition: transitions.default,
              '&:hover': {
                transform: 'translateY(-4px) scale(1.02)',
                boxShadow: 6,
              },
            }}>
              <CardContent>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  {t('metrics.contactedCPEs', 'Contacted CPEs')}
                </Typography>
                <Typography variant="h3" color="warning.main">
                  <CountUp end={overview.contactedCPEs} />
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3} sx={{ animation: animations.scaleIn, animationDelay: getStaggerDelay(5) }}>
            <Card sx={{
              height: '100%',
              minHeight: 140,
              transition: transitions.default,
              '&:hover': {
                transform: 'translateY(-4px) scale(1.02)',
                boxShadow: 6,
              },
            }}>
              <CardContent>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  {t('metrics.overdueNextActions', 'Overdue Next Actions')}
                </Typography>
                <Typography variant="h3" color={overview.overdueNextActions > 0 ? 'error.main' : 'success.main'}>
                  <CountUp end={overview.overdueNextActions} />
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3} sx={{ animation: animations.scaleIn, animationDelay: getStaggerDelay(6) }}>
            <Card sx={{
              height: '100%',
              minHeight: 140,
              transition: transitions.default,
              '&:hover': {
                transform: 'translateY(-4px) scale(1.02)',
                boxShadow: 6,
              },
            }}>
              <CardContent>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  {t('metrics.omContracted', 'O&M Contracted')}
                </Typography>
                <Typography variant="h3" color="success.main">
                  <CountUp end={overview.omContracted} />
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  <CountUp end={Math.round(overview.omContractedPower)} /> MW
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3} sx={{ animation: animations.scaleIn, animationDelay: getStaggerDelay(7) }}>
            <Card sx={{
              height: '100%',
              minHeight: 140,
              transition: transitions.default,
              '&:hover': {
                transform: 'translateY(-4px) scale(1.02)',
                boxShadow: 6,
              },
            }}>
              <CardContent>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  {t('metrics.recentCPEs', 'New CPEs (30 days)')}
                </Typography>
                <Typography variant="h3" color="info.main">
                  <CountUp end={overview.recentCPEs} />
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* By Plant Type and CPE Status */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={6}>
            <Typography variant="h5" gutterBottom sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <CategoryIcon />
              {t('metrics.byPlantType', 'By Plant Type')}
            </Typography>
            <TableContainer
              component={Paper}
              sx={{
                maxHeight: 340,
                overflowY: 'auto',
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: 'transparent',
                  marginTop: '56px',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: (theme) =>
                    theme.palette.mode === 'dark'
                      ? 'rgba(255, 255, 255, 0.2)'
                      : 'rgba(0, 0, 0, 0.2)',
                  borderRadius: '4px',
                  '&:hover': {
                    backgroundColor: (theme) =>
                      theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.3)'
                        : 'rgba(0, 0, 0, 0.3)',
                  },
                },
                '&:not(:hover)::-webkit-scrollbar-thumb': {
                  backgroundColor: 'transparent',
                },
              }}
            >
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>{t('metrics.plantType', 'Plant Type')}</strong></TableCell>
                    <TableCell align="right"><strong>{t('metrics.count', 'Count')}</strong></TableCell>
                    <TableCell align="right"><strong>{t('metrics.totalPower', 'Total Power (MW)')}</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(byPlantType).map(([type, count]) => (
                    <TableRow key={type}>
                      <TableCell>
                        <Chip label={type} color="primary" size="small" />
                      </TableCell>
                      <TableCell align="right">{count}</TableCell>
                      <TableCell align="right">{Math.round(powerByPlantType[type] || 0).toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>

          {/* By CPE Status */}
          <Grid item xs={12} md={6}>
            <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
              {t('metrics.byCPEStatus', 'By CPE Status')}
            </Typography>
            <TableContainer
              component={Paper}
              sx={{
                maxHeight: 340,
                overflowY: 'auto',
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: 'transparent',
                  marginTop: '56px',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: (theme) =>
                    theme.palette.mode === 'dark'
                      ? 'rgba(255, 255, 255, 0.2)'
                      : 'rgba(0, 0, 0, 0.2)',
                  borderRadius: '4px',
                  '&:hover': {
                    backgroundColor: (theme) =>
                      theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.3)'
                        : 'rgba(0, 0, 0, 0.3)',
                  },
                },
                '&:not(:hover)::-webkit-scrollbar-thumb': {
                  backgroundColor: 'transparent',
                },
              }}
            >
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>{t('metrics.status', 'Status')}</strong></TableCell>
                    <TableCell align="right"><strong>{t('metrics.count', 'Count')}</strong></TableCell>
                    <TableCell align="right"><strong>{t('metrics.totalPower', 'Total Power (MW)')}</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(byCPEStatus).map(([status, count]) => (
                    <TableRow key={status}>
                      <TableCell>{status}</TableCell>
                      <TableCell align="right">{count}</TableCell>
                      <TableCell align="right">{Math.round(powerByCPEStatus[status] || 0).toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </Grid>

        {/* Commercial Status & Internal Positioning */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>
              {t('metrics.byCommercialStatus', 'By Commercial Status')}
            </Typography>
            <TableContainer
              component={Paper}
              sx={{
                maxHeight: 340,
                overflowY: 'auto',
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: 'transparent',
                  marginTop: '56px',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: (theme) =>
                    theme.palette.mode === 'dark'
                      ? 'rgba(255, 255, 255, 0.2)'
                      : 'rgba(0, 0, 0, 0.2)',
                  borderRadius: '4px',
                  '&:hover': {
                    backgroundColor: (theme) =>
                      theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.3)'
                        : 'rgba(0, 0, 0, 0.3)',
                  },
                },
                '&:not(:hover)::-webkit-scrollbar-thumb': {
                  backgroundColor: 'transparent',
                },
              }}
            >
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>{t('metrics.status', 'Status')}</strong></TableCell>
                    <TableCell align="right"><strong>{t('metrics.count', 'Count')}</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(byCommercialStatus).map(([status, count]) => (
                    <TableRow key={status}>
                      <TableCell>{status}</TableCell>
                      <TableCell align="right">{count}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>
              {t('metrics.byInternalPositioning', 'By Internal Positioning')}
            </Typography>
            <TableContainer
              component={Paper}
              sx={{
                maxHeight: 340,
                overflowY: 'auto',
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: 'transparent',
                  marginTop: '56px',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: (theme) =>
                    theme.palette.mode === 'dark'
                      ? 'rgba(255, 255, 255, 0.2)'
                      : 'rgba(0, 0, 0, 0.2)',
                  borderRadius: '4px',
                  '&:hover': {
                    backgroundColor: (theme) =>
                      theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.3)'
                        : 'rgba(0, 0, 0, 0.3)',
                  },
                },
                '&:not(:hover)::-webkit-scrollbar-thumb': {
                  backgroundColor: 'transparent',
                },
              }}
            >
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>{t('metrics.positioning', 'Positioning')}</strong></TableCell>
                    <TableCell align="right"><strong>{t('metrics.count', 'Count')}</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>
                      <Chip label="Go" color="success" size="small" />
                    </TableCell>
                    <TableCell align="right">{byInternalPositioning.go}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <Chip label="No-Go" color="error" size="small" />
                    </TableCell>
                    <TableCell align="right">{byInternalPositioning.no_go}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <Chip label="Not Set" color="default" size="small" />
                    </TableCell>
                    <TableCell align="right">{byInternalPositioning.not_set}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </Grid>

        {/* By Energization Year and Top Account Managers */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={6}>
            <Typography variant="h5" gutterBottom sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <TimelineIcon />
              {t('metrics.byEnergizationYear', 'By Energization Year')}
            </Typography>
            <TableContainer
              component={Paper}
              sx={{
                maxHeight: 340,
                overflowY: 'auto',
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: 'transparent',
                  marginTop: '56px',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: (theme) =>
                    theme.palette.mode === 'dark'
                      ? 'rgba(255, 255, 255, 0.2)'
                      : 'rgba(0, 0, 0, 0.2)',
                  borderRadius: '4px',
                  '&:hover': {
                    backgroundColor: (theme) =>
                      theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.3)'
                        : 'rgba(0, 0, 0, 0.3)',
                  },
                },
                '&:not(:hover)::-webkit-scrollbar-thumb': {
                  backgroundColor: 'transparent',
                },
              }}
            >
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>{t('metrics.year', 'Year')}</strong></TableCell>
                    <TableCell align="right"><strong>{t('metrics.count', 'Count')}</strong></TableCell>
                    <TableCell align="right"><strong>{t('metrics.totalPower', 'Total Power (MW)')}</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(byEnergizationYear)
                    .sort((a, b) => Number(a[0]) - Number(b[0]))
                    .map(([year, count]) => (
                      <TableRow key={year}>
                        <TableCell>{year}</TableCell>
                        <TableCell align="right">{count}</TableCell>
                        <TableCell align="right">{Math.round(powerByEnergizationYear[Number(year)] || 0).toLocaleString()}</TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>

          {/* Top Account Managers */}
          <Grid item xs={12} md={6}>
            <Typography variant="h5" gutterBottom sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <PeopleIcon />
              {t('metrics.topAccountManagers', 'Top Account Managers')}
            </Typography>
            <TableContainer
              component={Paper}
              sx={{
                maxHeight: 340,
                overflowY: 'auto',
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: 'transparent',
                  marginTop: '56px',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: (theme) =>
                    theme.palette.mode === 'dark'
                      ? 'rgba(255, 255, 255, 0.2)'
                      : 'rgba(0, 0, 0, 0.2)',
                  borderRadius: '4px',
                  '&:hover': {
                    backgroundColor: (theme) =>
                      theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.3)'
                        : 'rgba(0, 0, 0, 0.3)',
                  },
                },
                '&:not(:hover)::-webkit-scrollbar-thumb': {
                  backgroundColor: 'transparent',
                },
              }}
            >
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>{t('metrics.accountManager', 'Account Manager')}</strong></TableCell>
                    <TableCell align="right"><strong>{t('metrics.cpeCount', 'CPE Count')}</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {topAccountManagers.map((manager, index) => (
                    <TableRow key={index}>
                      <TableCell>{manager.name}</TableCell>
                      <TableCell align="right">{manager.count}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};

